import { useQuery } from "@tanstack/react-query";
import { config } from "@/lib/config";

export interface MyAccessRequest {
    id: string;
    userId: string;
    tenantId: string;
    status: "pending" | "approved" | "denied" | "expired";
    message?: string;
    requestedAt: string;
    resolvedAt?: string;
    resolvedBy?: string;
    tenant?: {
        name: string;
    };
}

interface FetchMyAccessRequestsResponse {
    success: boolean;
    accessRequests: MyAccessRequest[];
}

// Custom error class for auth-related errors
class AuthTokenExpiredError extends Error {
    constructor(message: string = "Your session has expired. Please log in again.") {
        super(message);
        this.name = "AuthTokenExpiredError";
    }
}

async function fetchMyAccessRequests(
    temporaryToken: string,
    status?: "pending" | "approved" | "denied" | "expired",
): Promise<MyAccessRequest[]> {
    const url = new URL(`${config.apiUrl}/api/v1/my/access-requests`);
    if (status) {
        url.searchParams.set("status", status);
    }

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${temporaryToken}`,
        },
    });

    if (!response.ok) {
        // Handle token expiration specifically
        if (response.status === 401) {
            // Dispatch event to notify auth context of session expiry
            window.dispatchEvent(new CustomEvent("auth:session_expired"));
            throw new AuthTokenExpiredError();
        }
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch access requests");
    }

    const data: FetchMyAccessRequestsResponse = await response.json();
    return data.accessRequests;
}

export function useFetchMyAccessRequests(
    temporaryToken: string | null,
    status?: "pending" | "approved" | "denied" | "expired",
) {
    return useQuery({
        queryKey: ["myAccessRequests", temporaryToken, status],
        queryFn: () => fetchMyAccessRequests(temporaryToken!, status),
        enabled: !!temporaryToken,
        staleTime: 10000, // 10 seconds
        refetchInterval: 30000, // Refetch every 30 seconds to check for approval
    });
}
